import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import moment from 'moment-timezone'
import compose from 'recompose/compose'
import lifecycle from 'recompose/lifecycle'
import withProps from 'recompose/withProps'
import withHandlers from 'recompose/withHandlers'
import withState from 'recompose/withState'
import branch from 'recompose/branch'
import renderComponent from 'recompose/renderComponent'
import { List } from 'immutable'

import { isGmailConnected } from 'app/modules/user/selectors'
import * as PlaybookSelectors from 'app/modules/playbook/selectors'
import * as OrgSelectors from 'app/modules/org/selectors'
import * as OrgActions from 'app/modules/org/actions'
import * as PlaybookActions from 'app/modules/playbook/actions'
import * as InboxActions from 'app/modules/inbox/actions'
import * as InboxSelectors from 'app/modules/inbox/selectors'
import { playbooksCopy, emailSendModes, PlaybookStatus } from 'app/modules/playbook/constants'

import { makeHasGate } from 'app/modules/gates/selectors'

import { popUp } from 'components/Integration/helpers'
import { getApplication } from 'app/Application'
import { encodeQueryData } from 'utils/Helpers'
import { APP_URL, GOOGLE_CLIENT_ID, CHROME_EXTENSION_LINK } from 'constants/ResourcesConstants'
import { GoogleScopes } from 'app/modules/integrations/constants'
import { fetchGoogleScopesSuccess, fetchGoogleScopes } from 'app/modules/user/actions'
import * as UserPropertiesSelectors from 'app/modules/user/properties/selectors'

import SignatureEnable from '../SignatureEnable'

const APP = getApplication()
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth'

import Review from './component'

const mapStateToProps = state => ({
  userListIds: PlaybookSelectors.userListIds(state),
  excludedUserListIds: PlaybookSelectors.excludedUserListIds(state),
  draft: PlaybookSelectors.draft(state),
  orgId: OrgSelectors.orgId(state),
  reportType: PlaybookSelectors.reportType(state),
  isOneTimeEmailPlaybook: PlaybookSelectors.getIsOneTimeEmailPlaybook(state),
  isOutboundSequencePlaybook: PlaybookSelectors.getIsOutboundSequencePlaybook(state),
  draftScheduledTimestamp: PlaybookSelectors.dueAt(state),
  isEmailPlaybook: PlaybookSelectors.getIsEmailPlaybook(state),
  audience: PlaybookSelectors.audience(state),
  numConditions: PlaybookSelectors.getNumConditions(state),
  isCardinalityLoading: PlaybookSelectors.cardinalityLoading(state),
  targetingCardinality: PlaybookSelectors.targetingCardinality(state),
  smartFilters: PlaybookSelectors.smartFilters(state),
  isGmailConnected: isGmailConnected(state),
  userProperties: UserPropertiesSelectors.properties(state),
  isMarketingSequencePlaybook: PlaybookSelectors.getIsMarketingSequencePlaybook(state),
  isSignaturePlaybook: PlaybookSelectors.isSignaturePlaybook(state),
  hasInboxPicker: makeHasGate('has_playbook_inbox_picker')(state),
  senderEmailOverride: PlaybookSelectors.sequenceSenderEmailOverride(state),
  sendingDomains: InboxSelectors.sendingDomains(state),
  isSendingDomainsLoading: InboxSelectors.isSendingDomainsLoading(state),
  hasLimitedContactsMktEmail: OrgSelectors.hasLimitedContactsMktEmail(state)
})


const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchPlaybookCardinality: PlaybookActions.fetchPlaybookCardinality,
      showScheduleModal: PlaybookActions.showScheduleModal,
      setDraftSchedule: PlaybookActions.setDraftSchedule,
      setDraftStatus: PlaybookActions.setDraftStatus,
      showSequenceInviteModal: PlaybookActions.showSequenceInviteModal,
      showGoogleAuthModal: PlaybookActions.showGoogleAuthModal,
      showSendEmailConfirmModal: PlaybookActions.showSendEmailConfirmModal,
      fetchGoogleScopes: fetchGoogleScopes,
      fetchSendingDomains: InboxActions.fetchSendingDomains,
      fetchGoogleScopesSuccess,
      hasLimitedContactsMktEmail: OrgActions.hasLimitedContactsMktEmail
    },
    dispatch
  ),
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(props => props.isSignaturePlaybook, renderComponent(SignatureEnable)),
  lifecycle({
    componentWillMount() {
      this.props.actions.fetchGoogleScopes()
      this.props.actions.fetchSendingDomains()
      this.props.actions.hasLimitedContactsMktEmail()
    },
    componentDidMount() {
      const { userListIds, excludedUserListIds, orgId, smartFilters, isEmailPlaybook } = this.props
      this.props.actions.fetchPlaybookCardinality({ orgId, userListIds, excludedUserListIds, smartFilters, isEmailPlaybook })
    },
  }),
  withState(
    'selectedEmailSendMode',
    'setEmailSendMode',
    props => (!!props.draftScheduledTimestamp ? emailSendModes.SCHEDULE : emailSendModes.NOW)
  ),
  withProps(props => {

    const doGmailConnect = () => {
      const scope = `${GoogleScopes.READ} ${GoogleScopes.SEND}`
      const params = {
        scope,
        redirect_uri: `${APP_URL}/accept-integration/google-calendar`,
        client_id: GOOGLE_CLIENT_ID,
        response_type: 'code',
        access_type: 'offline',
        include_granted_scopes: true,
      }

      popUp({
        path: `${GOOGLE_AUTH_URL}?${encodeQueryData(params)}`,
        onMessage: handlePostMessage,
      })
    }

    const handlePostMessage = e => {
      const success = e.data.status === 'success'

      if (success) {
        APP.actions.NotificationActions.pushNotification({
          content: 'Your Google account was successfully connected.',
        })
        props.actions.fetchGoogleScopesSuccess(List.of(GoogleScopes.READ, GoogleScopes.SEND))

      } else {
        APP.actions.NotificationActions.pushNotification({
          content: (
            <span>
              Your Google account may be connected to a different Drift account. Please review your{' '}
              <a
                style={{ color: '#EFEFEF', textDecoration: 'underline' }}
                href="https://myaccount.google.com/permissions"
                target="_blank"
              >
                Google app permissions
              </a>.
            </span>
          ),
          variant: 'red',
          sticky: true,
        })
      }
    }


    const sendFromDomain = !!props.senderEmailOverride && props.senderEmailOverride.split('@').length > 1 ? props.senderEmailOverride.split('@')[1] : null
    const isDkimSetUp = props.sendingDomains.find(sendingDomain => sendingDomain.domain === sendFromDomain)
    const isDkimVerified = isDkimSetUp && isDkimSetUp.status === 'VERIFIED'

    return {
      hasSegmentSelected: !!props.userListIds && props.userListIds.size > 0,
      isSendNow: props.selectedEmailSendMode === emailSendModes.NOW,
      isScheduled: props.selectedEmailSendMode === emailSendModes.SCHEDULE,
      copy: playbooksCopy[props.reportType].review,
      draftScheduledMoment: moment(props.draftScheduledTimestamp).tz(moment.tz.guess()),
      isDkimSetUp: !!isDkimSetUp || props.isSendingDomainsLoading,
      isDkimVerified: !!isDkimVerified || props.isSendingDomainsLoading,
      handlePostMessage,
      doGmailConnect
    }
  }),
  withHandlers({
    handleShowScheduleModal: props => () => {
      props.actions.showScheduleModal()
    },
    handleSetNow: props => () => {
      props.setEmailSendMode(emailSendModes.NOW)
      props.actions.setDraftSchedule(null)
      if (props.draft.status !== PlaybookStatus.SENT)
        props.actions.setDraftStatus({ status: PlaybookStatus.DISABLED }) // in case we're editing
    },
    handleSetScheduled: props => () => {
      props.setEmailSendMode(emailSendModes.SCHEDULE)
      if (!props.draftScheduledTimestamp) {
        // set a default when the user selects
        // option if no date available
        props.actions.setDraftSchedule(
          moment()
            .add(2, 'days')
            .tz(moment.tz.guess())
        )
      }
    },
    onEnableConfirm: props => () => {
      const { showSendEmailConfirmModal } = props.actions
      if (props.isEmailPlaybook && !props.isOutboundSequencePlaybook && !props.checked) {
        showSendEmailConfirmModal()
      }
    },
    isExtensionInstalled: props => props.userProperties.get('extensionInstalled'),
    onShareClick: props => () => props.actions.showSequenceInviteModal(),
    onConnectGmailClick: props => () => props.doGmailConnect(),
    onInstallExtensionClick: () => () => {
      window.open(CHROME_EXTENSION_LINK, '_blank')
    },
  })
)(Review)



// WEBPACK FOOTER //
// ./src/scripts/components/playbook/components/Review/index.js